/**
 * Live-session WebSocket protocol (docs/07-tech-architecture.md §5).
 *
 * Both directions are discriminated unions validated with Zod on receipt, so a malformed
 * or hostile frame can never reach the session state machine.
 */

import { z } from 'zod';
import { CorrectionSchema, UtteranceFeedbackSchema } from './schemas/domain.js';
import {
  DialectSchema,
  EpochMsSchema,
  SessionModeSchema,
  UlidSchema,
} from './schemas/primitives.js';

export const WS_PATH = '/sessions/:sessionId/live';

// ── Client → server ───────────────────────────────────────────────────────────

export const ClientHelloSchema = z.object({
  t: z.literal('hello'),
  sessionId: UlidSchema,
  courseId: UlidSchema,
  mode: SessionModeSchema,
  /** Resume point when reconnecting; the server replays from its checkpoint. */
  resumeFromTurn: z.number().int().nonnegative().optional(),
  clientCaps: z.object({
    opus: z.boolean(),
    sampleRate: z.union([z.literal(16000), z.literal(24000), z.literal(48000)]),
    /** Whether the client can do on-device VAD, which saves us uplink and latency. */
    vad: z.boolean(),
  }),
});

export const ClientAudioSchema = z.object({
  t: z.literal('audio'),
  seq: z.number().int().nonnegative(),
  /** Base64 Opus frame, 20 ms. */
  b64: z.string().max(8192),
});

export const ClientMessageSchema = z.discriminatedUnion('t', [
  ClientHelloSchema,
  ClientAudioSchema,
  z.object({ t: z.literal('speech_end'), seq: z.number().int().nonnegative() }),
  /** Barge-in: cancels tutor TTS within 80 ms. */
  z.object({ t: z.literal('barge_in') }),
  z.object({ t: z.literal('hint') }),
  z.object({ t: z.literal('translate') }),
  z.object({ t: z.literal('repeat') }),
  z.object({ t: z.literal('slow') }),
  z.object({ t: z.literal('text'), value: z.string().max(2000) }),
  /** Switch to text mode mid-session, e.g. the learner moved somewhere loud. */
  z.object({ t: z.literal('switch_mode'), mode: SessionModeSchema }),
  z.object({ t: z.literal('ping') }),
  z.object({ t: z.literal('end'), reason: z.enum(['user', 'criteria_met', 'timeout']).optional() }),
]);
export type ClientMessage = z.infer<typeof ClientMessageSchema>;

// ── Server → client ───────────────────────────────────────────────────────────

export const ServerMessageSchema = z.discriminatedUnion('t', [
  z.object({
    t: z.literal('ready'),
    turn: z.number().int().nonnegative(),
    totalTurns: z.number().int().nonnegative().nullable(),
    personaName: z.string(),
    /**
     * The course's dialect, for a client reading a line in its own voice.
     *
     * Session context, like `personaName` beside it: `es-MX` and `es-ES` are different
     * voices on every platform synthesiser, so a client that only knows the language
     * reads Mexican Spanish in a Castilian accent to a learner who chose otherwise.
     */
    dialect: DialectSchema.optional(),
  }),
  /** Streamed tutor text; render incrementally with grapheme-safe boundaries. */
  z.object({
    t: z.literal('tutor_text'),
    turn: z.number().int().nonnegative(),
    delta: z.string(),
    done: z.boolean(),
    /**
     * Whether the server will send `tutor_audio` for this turn.
     *
     * Sent on the final frame so a client knows, rather than guesses from a silence,
     * whether to read the line in the device's own voice. The alternative is a timer
     * racing frames that may simply be slow, which is a guess dressed as a timeout.
     *
     * False whenever no speech engine is configured, or none of them reads this
     * dialect. Both are ordinary states: the tutor was silent on every client in
     * exactly those cases, while two of the three held an offline voice already.
     */
    voiced: z.boolean().optional(),
  }),
  z.object({
    t: z.literal('tutor_audio'),
    turn: z.number().int().nonnegative(),
    seq: z.number().int().nonnegative(),
    b64: z.string(),
    done: z.boolean(),
  }),
  z.object({
    t: z.literal('transcript'),
    turn: z.number().int().nonnegative(),
    text: z.string(),
    partial: z.boolean(),
  }),
  z.object({
    t: z.literal('score'),
    turn: z.number().int().nonnegative(),
    feedback: UtteranceFeedbackSchema,
  }),
  z.object({
    t: z.literal('correction'),
    turn: z.number().int().nonnegative(),
    corrections: z.array(CorrectionSchema),
  }),
  /**
   * Three things the learner could say next, shortest first.
   *
   * The client could always *ask* for a hint and never had anywhere to receive one:
   * `{ t: 'hint' }` existed on the way up with no frame on the way down, so the button
   * sent a request into a void. The recorded competitor evidence is that this scaffold
   * is what stops a learner facing a blank microphone
   * (docs/research/10-competitor-teardown-2026-10-18.md §6).
   */
  z.object({
    t: z.literal('hint'),
    turn: z.number().int().nonnegative(),
    options: z
      .array(z.object({ text: z.string().max(160), translation: z.string().max(200) }))
      .max(3),
  }),
  /** The tutor's last line in the learner's own language, on request. */
  z.object({
    t: z.literal('translation'),
    turn: z.number().int().nonnegative(),
    text: z.string().max(600),
  }),
  z.object({ t: z.literal('criterion_met'), id: z.string() }),
  z.object({
    t: z.literal('quota'),
    voiceSecondsLeft: z.number().nullable(),
    state: z.enum(['unlimited', 'ample', 'low', 'exhausted']),
  }),
  /** Told plainly, and the session keeps going in text mode. */
  z.object({
    t: z.literal('degrade'),
    reason: z.enum([
      'network',
      'provider_unavailable',
      'audio_session',
      'permission',
      'quota',
      'timeout',
      'unknown',
    ]),
    message: z.string(),
  }),
  z.object({ t: z.literal('checkpoint'), turn: z.number().int().nonnegative() }),
  z.object({ t: z.literal('complete'), sessionId: UlidSchema, reportReady: z.boolean() }),
  z.object({ t: z.literal('pong'), at: EpochMsSchema }),
  z.object({
    t: z.literal('error'),
    problem: z.object({
      type: z.string(),
      title: z.string(),
      status: z.number().int(),
      detail: z.string().optional(),
      retryable: z.boolean(),
    }),
  }),
]);
export type ServerMessage = z.infer<typeof ServerMessageSchema>;

/** Parses an inbound frame, returning `null` rather than throwing on malformed input. */
export function parseClientMessage(raw: string): ClientMessage | null {
  try {
    const result = ClientMessageSchema.safeParse(JSON.parse(raw));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export function parseServerMessage(raw: string): ServerMessage | null {
  try {
    const result = ServerMessageSchema.safeParse(JSON.parse(raw));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export function encode(message: ClientMessage | ServerMessage): string {
  return JSON.stringify(message);
}

/** Protocol limits, enforced on both ends. */
export const WS_LIMITS = {
  maxFrameBytes: 16_384,
  /** Idle sessions hibernate; the DO alarm reaps them. */
  idleTimeoutMs: 120_000,
  pingIntervalMs: 20_000,
  /** Hard cap on a single live session, matching the UI's stated 15 minutes. */
  maxSessionMs: 900_000,
  maxAudioFramesPerSecond: 60,
} as const;
